import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatCents, useAccounts, useCategories } from "@/lib/money";
import { Plus, Trash2, Pencil, Play, Pause, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FREQS = ["weekly", "fortnightly", "monthly", "quarterly", "yearly"];
const DIRECTIONS = ["expense", "income"];

export default function MoneyRecurring() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { accounts } = useAccounts();
  const { categories } = useCategories();

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/money/recurring");
    setRules(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (rule) => {
    await api.patch(`/money/recurring/${rule.id}`, { active: !rule.active });
    toast.success(rule.active ? "Paused" : "Resumed");
    load();
  };
  const del = async (rule) => {
    if (!confirm(`Delete "${rule.name}"?`)) return;
    await api.delete(`/money/recurring/${rule.id}`);
    toast.success("Deleted");
    load();
  };

  const subs = rules.filter((r) => r.is_subscription && r.active);
  const subsTotal = subs.reduce((s, r) => s + r.amount_cents, 0);
  const activeMonthly = rules.filter((r) => r.active && r.frequency === "monthly").reduce((s, r) => s + r.amount_cents, 0);

  return (
    <div data-testid="money-recurring" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-hero bg-surface-container p-6">
          <div className="text-xs uppercase tracking-widest text-surface-onVariant">Monthly commitment</div>
          <div className="text-3xl font-medium text-surface-on tabular-nums mt-1" data-testid="monthly-commitment">{formatCents(activeMonthly)}</div>
          <div className="text-xs text-surface-onVariant mt-1">{rules.filter((r) => r.active).length} active rules</div>
        </div>
        <div className="rounded-hero bg-surface-container p-6">
          <div className="text-xs uppercase tracking-widest text-surface-onVariant">Subscriptions</div>
          <div className="text-3xl font-medium text-surface-on tabular-nums mt-1" data-testid="subs-total">{formatCents(subsTotal)}<span className="text-base text-surface-onVariant">/mo</span></div>
          <div className="text-xs text-surface-onVariant mt-1">{subs.length} active subscriptions</div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-surface-on">All recurring rules</h3>
        <button
          data-testid="rule-add-open"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-medium px-5 py-2.5 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
        ><Plus className="w-4 h-4" /> Add rule</button>
      </div>

      <div className="rounded-hero bg-surface-container overflow-hidden">
        {loading && <div className="p-6 text-sm text-surface-onVariant">Loading…</div>}
        {!loading && rules.length === 0 && <div className="p-10 text-center text-sm text-surface-onVariant">No recurring rules yet.</div>}
        <ul className="divide-y divide-outline-variant" data-testid="rules-list">
          {rules.map((r) => {
            const acc = accounts.find((a) => a.id === r.account_id);
            const cat = categories.find((c) => c.id === r.category_id);
            return (
              <li key={r.id} data-testid={`rule-row-${r.id}`} className={cn("px-6 py-4 flex items-center gap-4", !r.active && "opacity-60")}>
                <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                    r.is_subscription ? "bg-tertiary-container text-tertiary-onContainer" : "bg-primary-container text-primary-onContainer")}>
                  <Repeat className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-surface-on truncate">{r.name}</div>
                    {r.is_subscription && <span className="text-[9px] font-medium tracking-wider uppercase bg-tertiary-container text-tertiary-onContainer px-2 py-0.5 rounded-full">Sub</span>}
                    {!r.active && <span className="text-[9px] font-medium tracking-wider uppercase bg-surface-containerHigh text-surface-onVariant px-2 py-0.5 rounded-full">Paused</span>}
                  </div>
                  <div className="text-xs text-surface-onVariant mt-0.5">
                    {r.frequency} · next {r.next_date} · lead {r.lead_days}d · {acc?.name || "—"}{cat ? ` · ${cat.name}` : ""}
                  </div>
                </div>
                <div className="text-sm font-medium text-surface-on tabular-nums whitespace-nowrap">
                  {r.direction === "income" ? "+" : "-"}{formatCents(r.amount_cents)}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`rule-toggle-${r.id}`}
                    onClick={() => toggleActive(r)}
                    aria-label={r.active ? "Pause" : "Resume"}
                    className="p-2 rounded-full text-surface-onVariant hover:bg-primary/8 hover:text-primary transition-colors duration-200 ease-md"
                  >{r.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
                  <button
                    data-testid={`rule-edit-${r.id}`}
                    onClick={() => { setEditing(r); setDialogOpen(true); }}
                    aria-label="Edit"
                    className="p-2 rounded-full text-surface-onVariant hover:bg-primary/8 hover:text-primary transition-colors duration-200 ease-md"
                  ><Pencil className="w-4 h-4" /></button>
                  <button
                    data-testid={`rule-delete-${r.id}`}
                    onClick={() => del(r)}
                    aria-label="Delete"
                    className="p-2 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger transition-colors duration-200 ease-md"
                  ><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <RuleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        rule={editing}
        accounts={accounts}
        categories={categories}
        onSaved={load}
      />
    </div>
  );
}

function RuleDialog({ open, onOpenChange, rule, accounts, categories, onSaved }) {
  const isEdit = !!rule;
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState("expense");
  const [frequency, setFrequency] = useState("monthly");
  const [nextDate, setNextDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [leadDays, setLeadDays] = useState(7);
  const [isSub, setIsSub] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      setName(rule.name); setAmount((rule.amount_cents / 100).toFixed(2));
      setDirection(rule.direction); setFrequency(rule.frequency); setNextDate(rule.next_date);
      setAccountId(rule.account_id); setCategoryId(rule.category_id || "");
      setLeadDays(rule.lead_days); setIsSub(rule.is_subscription);
    } else {
      setName(""); setAmount(""); setDirection("expense"); setFrequency("monthly");
      setNextDate(new Date().toISOString().slice(0, 10));
      setAccountId(""); setCategoryId(""); setLeadDays(7); setIsSub(false);
    }
  }, [open, rule]);

  const cats = categories.filter((c) => (direction === "income" ? c.kind === "income" : c.kind === "expense"));

  const save = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!name.trim()) return toast.error("Name required");
    if (cents <= 0) return toast.error("Amount required");
    if (!accountId) return toast.error("Pick an account");
    if (!categoryId) return toast.error("Pick a category");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), amount_cents: cents, direction, frequency,
        next_date: nextDate, account_id: accountId, category_id: categoryId,
        lead_days: parseInt(leadDays) || 7, is_subscription: isSub, active: true,
      };
      if (isEdit) await api.patch(`/money/recurring/${rule.id}`, payload);
      else await api.post("/money/recurring", payload);
      toast.success(isEdit ? "Rule updated" : "Rule created");
      onOpenChange(false); onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-surface rounded-hero border-none p-6" data-testid="rule-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">{isEdit ? "Edit rule" : "Add recurring rule"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <Field label="Name">
            <input data-testid="rule-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rent, Netflix, BAS" className="bg-transparent outline-none w-full text-surface-on" required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" prefix="$">
              <input data-testid="rule-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" placeholder="0.00" required />
            </Field>
            <Field label="Next date">
              <input data-testid="rule-next-date" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" required />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="rule-direction" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  {DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="rule-frequency" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-2xl bg-surface">
                  {FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Account</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="rule-account" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue placeholder="Choose account" /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface">
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="rule-category" className="w-full rounded-t-lg rounded-b-none bg-surface-containerHigh border-b-2 border-outline focus:border-primary h-11"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent className="rounded-2xl bg-surface max-h-72">
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lead days">
              <input data-testid="rule-lead" type="number" min="0" max="60" value={leadDays} onChange={(e) => setLeadDays(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" />
            </Field>
            <label className="flex items-center gap-2 bg-surface-containerHigh rounded-t-lg border-b-2 border-outline px-3 py-3 cursor-pointer">
              <input data-testid="rule-is-sub" type="checkbox" checked={isSub} onChange={(e) => setIsSub(e.target.checked)} className="accent-primary" />
              <span className="text-sm text-surface-on">Subscription</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 rounded-full text-sm font-medium text-surface-on hover:bg-primary/8">Cancel</button>
            <button data-testid="rule-save" type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create rule"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">{children}</div>;
}
function Field({ label, prefix, children }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5 transition-colors duration-200 ease-md">
        {prefix && <span className="text-surface-onVariant mr-1">{prefix}</span>}
        {children}
      </div>
    </div>
  );
}
